import { useState, useRef, useEffect, useMemo } from 'react';
import './SearchableSelect.css';

export interface SelectOption {
  value: number;
  label: string;
}

interface Props {
  options: SelectOption[];
  value: number | '';
  onChange: (value: number | '') => void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
  /**
   * Si se pasa, cuando la búsqueda no encuentra nada se ofrece crear la opción
   * con el texto tecleado. Recibe ese texto y devuelve el valor ya creado (o
   * null si se cancela / falla), que se selecciona automáticamente.
   */
  onCreate?: (label: string) => Promise<number | null>;
  /** Texto del botón de alta. Recibe el texto tecleado. */
  createLabel?: (query: string) => string;
}

// Normaliza para buscar: sin acentos/diacríticos y en minúsculas. Sin esto,
// escribir "cebada" no encontraría "Cebada" y "MAIZ" no encontraría "Maíz".
function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

// Selector con búsqueda por tokens, insensible a mayúsculas y acentos:
// "alf mix" encuentra "ALFALFA MIX B.B." aunque las palabras no vayan seguidas.
// Reutilizable para producto/proveedor del formulario self-managed.
export function SearchableSelect({
  options, value, onChange, placeholder, disabled, emptyText, onCreate, createLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.value === value);
  const displayText = selected ? selected.label : '';

  // Índice normalizado precalculado: con cientos de opciones evita rehacer el
  // normalize() de cada etiqueta en cada pulsación de tecla.
  const indexed = useMemo(
    () => options.map((o) => ({ opt: o, haystack: normalize(o.label) })),
    [options]
  );

  const tokens = normalize(query).split(/\s+/).filter(Boolean);
  const filtered = tokens.length === 0
    ? options
    : indexed.filter(({ haystack }) => tokens.every((tk) => haystack.includes(tk))).map((x) => x.opt);

  const trimmedQuery = query.trim();
  const canCreate = !!onCreate && trimmedQuery.length > 0 && filtered.length === 0;

  const handleCreate = async () => {
    if (!onCreate || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(trimmedQuery);
      if (created != null) {
        onChange(created);
        setOpen(false);
        setQuery('');
      }
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const select = (o: SelectOption) => {
    onChange(o.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="searchable-select" ref={ref}>
      <input
        className="cp-input searchable-select__input"
        type="text"
        value={open ? query : displayText}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
      />
      {open && !disabled && (
        <ul className="searchable-select__list">
          {filtered.length === 0 && !canCreate && (
            <li className="searchable-select__empty">{emptyText ?? '—'}</li>
          )}
          {canCreate && (
            <li
              className="searchable-select__create"
              onMouseDown={(e) => { e.preventDefault(); void handleCreate(); }}
            >
              {creating
                ? '…'
                : (createLabel ? createLabel(trimmedQuery) : `+ ${trimmedQuery}`)}
            </li>
          )}
          {filtered.slice(0, 100).map((o) => (
            <li
              key={o.value}
              className={`searchable-select__opt${o.value === value ? ' is-selected' : ''}`}
              // onMouseDown + preventDefault: seleccionar antes de que el blur del input cierre la lista.
              onMouseDown={(e) => { e.preventDefault(); select(o); }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
